// HELP CHOOSING (30 Oct 2026): links from the pages Google already shows for
// our searches (product, price-list and dealer pages) to the AC guides that
// answer the question a buyer has on that page.
//
// Why: the product and price pages rank; the guides were days old and had no
// links from them (the Optimist lesson, 28 Oct 2026, project doc
// claude/blog-rankings-optimist.md). GUIDES_PLAYBOOK.md rule: every new guide
// gets a link from the matching pages here.
//
// The same links go into the pre-rendered HTML (scripts/prerender.js), so
// crawlers see them before JavaScript runs. Only live guides are linked: pass
// the live slugs from GUIDES in data/guides.js.
//
// Adding a guide: give it an entry in HELP_GUIDES and put its key in the
// lists below for the pages it fits.
//
// No imports: scripts/prerender.js loads this file by stripping `export`.

export const HELP_TITLE = 'Help choosing';

// Short titles in the words people search, and a one-line hint. No figures
// here: the numbers live in the guides, with their sources.
export const HELP_GUIDES = {
  stars: { slug: '3-star-vs-5-star-ac', title: '3 Star or 5 Star AC?', text: 'Which one pays back for the hours you use it.' },
  size: { slug: 'what-size-ac-do-i-need', title: 'What size AC do I need?', text: 'The right ton for your room and floor.' },
  inverter: { slug: 'inverter-vs-non-inverter-ac', title: 'Inverter or non-inverter AC?', text: 'Which one suits your room and your hours.' },
  units: { slug: 'ac-power-consumption-units-per-hour', title: 'How many units does an AC use?', text: 'What it adds to your bill each hour.' },
  install: { slug: 'ac-installation-cost', title: 'How much does AC installation cost?', text: 'Our rate card, item by item.' },
  heat: { slug: 'hot-and-cold-ac', title: 'Is a hot and cold AC worth it?', text: 'When one AC should heat your room in winter too.' },
  vrvhome: { slug: 'vrv-system-for-home', title: 'Which VRV for your home?', text: 'VRV Home, VRV S or VRV Alpha, and the cost.' },
  vrv: { slug: 'vrv-vs-split-ac', title: 'VRV or split AC for your home?', text: 'When one system beats an AC in every room.' },
};

const MAX_LINKS = 3;

function pick(keys, liveSlugs) {
  const live = new Set(liveSlugs || []);
  return keys
    .map((k) => HELP_GUIDES[k])
    .filter((g) => g && live.has(g.slug))
    .slice(0, MAX_LINKS)
    .map((g) => ({ href: `/blog/${g.slug}`, title: g.title, text: g.text }));
}

// A split AC product page (a CATALOGUE model).
export function helpForModel(m, liveSlugs) {
  const keys = [];
  if (m.series === 'FTHT') keys.push('heat');
  if (m.inverter && (m.stars === 3 || m.stars === 5)) keys.push('stars');
  keys.push('size', 'inverter', 'units');
  return pick(keys, liveSlugs);
}

// A price-list (collection) page, by its filter in data/collections.js.
export function helpForCollection(c, liveSlugs) {
  const f = (c && c.filter) || {};
  let keys;
  if (f.stars != null) keys = ['stars', 'size', 'units'];
  else if (f.inverter === true) keys = ['inverter', 'stars', 'size'];
  else if (f.inverter === false) keys = ['inverter', 'units', 'size'];
  else if (f.series === 'FTHT') keys = ['heat', 'size', 'units'];
  else if (f.series) keys = ['size', 'units', 'install'];
  else if (f.soldAs) keys = ['size', 'stars', 'units'];
  else keys = ['size', 'stars', 'inverter'];
  return pick(keys, liveSlugs);
}

// The main price list, /daikin-ac-price-list-delhi.
export const helpForPriceList = (liveSlugs) => pick(['size', 'stars', 'inverter'], liveSlugs);

// A dealer (location) page, by pageKind() in data/locationExtras.js. O-General
// pages get none: the guides are about Daikin split ACs and VRV.
export function helpForLocation(kind, liveSlugs) {
  if (!kind || kind.oGeneral) return [];
  if (kind.vrv && kind.shop) return pick(['vrv', 'vrvhome', 'size'], liveSlugs);
  if (kind.vrv) return pick(['vrv', 'vrvhome', 'size'], liveSlugs);
  if (kind.shop) return pick(['size', 'inverter', 'install'], liveSlugs);
  return [];
}

// The same block for the pre-rendered HTML. `esc` escapes text.
export function helpChoosingHtml(links, esc) {
  if (!links || !links.length) return '';
  return `
            <h2 style="font-size:1.25rem;font-weight:bold;margin-top:24px;">${HELP_TITLE}</h2>
            <ul>${links.map((l) => `<li><a href="${esc(l.href)}">${esc(l.title)}</a>: ${esc(l.text)}</li>`).join('')}</ul>`;
}
